import React, { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  LayoutAnimation,
  Platform,
  UIManager,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";

// Enable LayoutAnimation on Android
if (Platform.OS === "android" && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

/**
 * Guide section interface
 */
interface GuideSection {
  id: string;
  icon: string;
  title: string;
  color: string;
  content: GuideItem[];
}

/**
 * Guide item interface
 */
interface GuideItem {
  title: string;
  description: string;
}

/**
 * All guide sections with content
 */
const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: "weekend",
    icon: "calendar",
    title: "Weekend Voorbereiden",
    color: "#7C3AED",
    content: [
      {
        title: "Naam, thema en datum instellen",
        description: "In het Algemeen scherm kun je de weekendnaam, thema en exacte data instellen. Deze informatie wordt getoond op het dashboard van alle gebruikers.",
      },
      {
        title: "Locatie instellen met kaart",
        description: "Klik op 'Locatie kiezen' om de kaart te openen. Zoek het adres of tik op de kaart om de locatie te selecteren. Je kunt de locatie verborgen houden tot een reveal.",
      },
      {
        title: "Banner personaliseren",
        description: "Kies een kleur en Lottie animatie voor de weekend banner. Dit wordt getoond op het hoofdscherm en maakt het weekend herkenbaar.",
      },
    ],
  },
  {
    id: "familiedag",
    icon: "sunny",
    title: "Familie Dag Beheren",
    color: "#F59E0B",
    content: [
      {
        title: "Familie Dag datum en locatie instellen",
        description: "In het Algemeen scherm scroll je naar 'Familie Dag'. Stel de datum en locatie in. Dit is een apart evenement van het hoofdweekend.",
      },
      {
        title: "Instructies toevoegen",
        description: "Voeg praktische instructies toe zoals inchecktijden, parkeerinformatie, of wat mee te nemen. Elke instructie heeft een titel, beschrijving en optioneel icoon.",
      },
      {
        title: "Locatie reveal instellen",
        description: "Je kunt de Familie Dag locatie verborgen houden tot een bepaald moment. Koppel een challenge aan de reveal zodat teams de locatie kunnen 'unlocken'.",
      },
      {
        title: "Challenge koppelen aan reveal",
        description: "Maak eerst een challenge aan in Spellen → Opdrachten. Daarna kun je deze koppelen aan de Familie Dag reveal zodat teams moeten meedoen om de locatie te zien.",
      },
    ],
  },
  {
    id: "teams",
    icon: "people",
    title: "Teams Beheren",
    color: "#10B981",
    content: [
      {
        title: "Teams aanmaken met naam en kleur",
        description: "Ga naar Teams → Nieuw Team. Geef het team een naam, kies een kleur en optioneel een slogan. Teams kunnen later hun eigen avatar maken.",
      },
      {
        title: "Leden toewijzen aan teams",
        description: "Klik op een team en dan 'Leden beheren'. Je ziet alle beschikbare gebruikers. Tik op een gebruiker om deze toe te voegen aan het team.",
      },
      {
        title: "Shuffle functie voor eerlijke verdeling",
        description: "Gebruik de shuffle functie om gebruikers willekeurig over teams te verdelen. Je kunt kiezen of admins mee worden verdeeld.",
      },
      {
        title: "Team captain aanwijzen",
        description: "In het team detail scherm kun je één lid als captain aanwijzen. De captain heeft geen speciale rechten maar is herkenbaar aan een kroon icoon.",
      },
      {
        title: "Voogden koppelen aan kinderen",
        description: "Kinderen zonder eigen account (dependents) kunnen worden gekoppeld aan voogden. De voogd kan dan 'spelen als' het kind om deel te nemen.",
      },
    ],
  },
  {
    id: "games",
    icon: "game-controller",
    title: "Spellen & Opdrachten",
    color: "#3B82F6",
    content: [
      {
        title: "Quiz aanmaken met vragen",
        description: "Ga naar Spellen → Quiz → Nieuwe Quiz. Stel de naam, beschrijving en regels in. Voeg daarna vragen toe met meerdere antwoordopties.",
      },
      {
        title: "Speurtocht met stappen",
        description: "Maak een speurtocht aan met meerdere stappen. Elke stap kan een locatie, foto-opdracht, puzzel, woord-invoer of QR-code scan zijn.",
      },
      {
        title: "Foto-opdrachten aanmaken",
        description: "Ga naar Spellen → Opdrachten → Nieuwe Opdracht. Kies type 'Foto' en beschrijf wat er op de foto moet staan. Teams uploaden hun foto ter beoordeling.",
      },
      {
        title: "Spellen activeren voor het weekend",
        description: "In het Spellen overzicht kun je spellen aan/uitzetten voor weekend modus. Stel ook de punten in die teams kunnen verdienen.",
      },
      {
        title: "Inzendingen beoordelen",
        description: "Bij elke opdracht zie je de inzendingen van teams. Keur ze goed of af. Bij goedkeuring worden automatisch punten toegekend.",
      },
    ],
  },
  {
    id: "agenda",
    icon: "calendar-outline",
    title: "Agenda & Reveals",
    color: "#EC4899",
    content: [
      {
        title: "Evenementen aanmaken",
        description: "Ga naar Agenda en tik op + om een evenement toe te voegen. Stel titel, beschrijving, locatie, datum en tijd in.",
      },
      {
        title: "Reveal types: countdown, hints, challenges",
        description: "Evenementen kunnen een reveal hebben. Kies uit countdown (tot bepaalde tijd), hints (progressief) of challenges (teams moeten iets doen).",
      },
      {
        title: "Hints toevoegen en plannen",
        description: "Bij hint-reveals kun je meerdere hints toevoegen met elk een release moment. Hints worden automatisch vrijgegeven op het ingestelde tijdstip.",
      },
      {
        title: "Notificaties bij reveal",
        description: "Je kunt instellen dat gebruikers een notificatie krijgen wanneer een reveal plaatsvindt. Dit houdt iedereen betrokken.",
      },
    ],
  },
  {
    id: "scores",
    icon: "trophy",
    title: "Punten & Scores",
    color: "#8B5CF6",
    content: [
      {
        title: "Handmatig punten toekennen/aftrekken",
        description: "Ga naar Punten → Punten Toekennen. Selecteer een team, vul het aantal punten in (negatief voor aftrekken) en geef een reden op.",
      },
      {
        title: "Punten per spel instellen",
        description: "In Spellen beheer kun je per spel de punten aanpassen. Stel punten voor winst, deelname en bonussen in.",
      },
      {
        title: "Scorebord en team statistieken",
        description: "Het scorebord is zichtbaar voor alle gebruikers. Je ziet de totale scores per team en individuele bijdragen.",
      },
      {
        title: "Score notificaties aanpassen",
        description: "In Notificaties → Instellingen kun je aanpassen hoe score-notificaties eruitzien. Kies thema's en animaties voor positieve en negatieve scores.",
      },
    ],
  },
  {
    id: "notifications",
    icon: "notifications",
    title: "Notificaties",
    color: "#14B8A6",
    content: [
      {
        title: "Broadcast berichten sturen",
        description: "Ga naar Notificaties → Broadcast om een bericht naar alle gebruikers te sturen. Je kunt een Lottie animatie toevoegen voor extra impact.",
      },
      {
        title: "Notificatie templates aanpassen",
        description: "Elke type notificatie (score, verjaardag, etc.) kan worden aangepast. Verander de tekst, kleuren en animaties.",
      },
      {
        title: "Geplande notificaties",
        description: "Je kunt notificaties vooruit plannen. Stel de datum en tijd in en de notificatie wordt automatisch verzonden.",
      },
      {
        title: "Notificatie thema's en animaties",
        description: "Kies uit 13 gradient thema's en tientallen Lottie animaties om notificaties visueel aantrekkelijk te maken.",
      },
    ],
  },
  {
    id: "faq",
    icon: "help-circle",
    title: "Veelgestelde Vragen",
    color: "#6B7280",
    content: [
      {
        title: "Hoe kan ik een team verwijderen?",
        description: "Ga naar Teams → kies het team → scroll naar beneden voor de verwijder optie. Let op: alle teamleden worden losgekoppeld.",
      },
      {
        title: "Kan ik punten achteraf aanpassen?",
        description: "Ja, je kunt handmatig punten toevoegen of aftrekken via Punten → Punten Toekennen. Geef een duidelijke reden op voor de administratie.",
      },
      {
        title: "Waar zie ik de activiteit van gebruikers?",
        description: "Super Admins kunnen alle activiteit bekijken via Super Admin → Logs. Hier zie je alle acties met tijdstempels.",
      },
      {
        title: "Hoe maak ik een kind-account aan?",
        description: "Alleen Super Admins kunnen dependent users aanmaken via Super Admin → Gebruikers → Nieuw Kind. Daarna kan een familie admin voogden koppelen.",
      },
      {
        title: "Wat gebeurt er als ik het weekend deactiveer?",
        description: "Het weekend blijft bestaan maar is niet meer actief. Gebruikers zien geen weekendbanner meer. Je kunt het later weer activeren.",
      },
    ],
  },
];

/**
 * Collapsible Section Component
 */
const CollapsibleSection: React.FC<{
  section: GuideSection;
  isExpanded: boolean;
  onToggle: () => void;
  colors: ReturnType<typeof useThemeColors>;
}> = ({ section, isExpanded, onToggle, colors }) => {
  return (
    <View 
      className="mb-3 rounded-2xl overflow-hidden"
      style={{ backgroundColor: colors.surface }}
    >
      {/* Header */}
      <TouchableOpacity
        onPress={onToggle}
        className="flex-row items-center p-4"
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`${section.title}, ${isExpanded ? "ingeklapt" : "uitgeklapt"}`}
      >
        <View
          className="w-10 h-10 rounded-full items-center justify-center mr-3"
          style={{ backgroundColor: `${section.color}20` }}
        >
          <Ionicons
            name={section.icon as any}
            size={20}
            color={section.color}
          />
        </View>
        <Text
          className="flex-1 font-semibold text-base"
          style={{ color: colors.text }}
        >
          {section.title}
        </Text>
        <Ionicons
          name={isExpanded ? "chevron-up" : "chevron-down"}
          size={20}
          color={colors.textSecondary}
        />
      </TouchableOpacity>

      {/* Content */}
      {isExpanded && (
        <View className="px-4 pb-4">
          {section.content.map((item, index) => (
            <View
              key={index}
              className="py-3"
              style={{
                borderTopWidth: index > 0 ? 1 : 0,
                borderTopColor: colors.border,
              }}
            >
              <Text
                className="font-medium text-sm mb-1"
                style={{ color: colors.text }}
              >
                {item.title}
              </Text>
              <Text
                className="text-sm leading-relaxed"
                style={{ color: colors.textSecondary }}
              >
                {item.description}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

/**
 * Admin Guide Screen
 * 
 * Comprehensive documentation for familie admins with collapsible sections
 * explaining all admin features step by step.
 */
export default function AdminGuideScreen() {
  const colors = useThemeColors();
  const [expandedSections, setExpandedSections] = useState<string[]>(["weekend"]);

  /**
   * Toggle section expansion
   */
  const handleToggleSection = (sectionId: string) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  /**
   * Expand all sections
   */
  const handleExpandAll = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedSections(GUIDE_SECTIONS.map((s) => s.id));
  };

  /**
   * Collapse all sections
   */
  const handleCollapseAll = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedSections([]);
  };

  const allExpanded = expandedSections.length === GUIDE_SECTIONS.length;

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ padding: 16 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View className="mb-6">
          <Text
            className="text-2xl font-bold mb-2"
            style={{ color: colors.text }}
          >
            📖 Handleiding
          </Text>
          <Text
            className="text-base leading-relaxed"
            style={{ color: colors.textSecondary }}
          >
            Stap-voor-stap uitleg voor alle admin functies. Tik op een sectie om
            deze uit te klappen.
          </Text>
        </View>

        {/* Expand/Collapse All Button */}
        <TouchableOpacity
          onPress={allExpanded ? handleCollapseAll : handleExpandAll}
          className="flex-row items-center justify-center py-2 mb-4 rounded-xl"
          style={{ backgroundColor: colors.surfaceSecondary }}
          accessibilityRole="button"
          accessibilityLabel={allExpanded ? "Alles inklappen" : "Alles uitklappen"}
        >
          <Ionicons
            name={allExpanded ? "contract-outline" : "expand-outline"}
            size={18}
            color={colors.primary}
          />
          <Text
            className="ml-2 font-medium"
            style={{ color: colors.primary }}
          >
            {allExpanded ? "Alles inklappen" : "Alles uitklappen"}
          </Text>
        </TouchableOpacity>

        {/* Sections */}
        {GUIDE_SECTIONS.map((section) => (
          <CollapsibleSection
            key={section.id}
            section={section}
            isExpanded={expandedSections.includes(section.id)}
            onToggle={() => handleToggleSection(section.id)}
            colors={colors}
          />
        ))}

        {/* Help Footer */}
        <View
          className="mt-4 p-4 rounded-2xl items-center"
          style={{ backgroundColor: `${colors.primary}10` }}
        >
          <Ionicons
            name="chatbubble-ellipses"
            size={32}
            color={colors.primary}
          />
          <Text
            className="font-semibold mt-2"
            style={{ color: colors.text }}
          >
            Nog vragen?
          </Text>
          <Text
            className="text-sm text-center mt-1"
            style={{ color: colors.textSecondary }}
          >
            Neem contact op met een super admin voor verdere hulp.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

